import { useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { alertActions } from '../redux/AlertController';
import useLogout from './useLogout';

// Authenticated JSON requests to the admin API: adds the token, logs out on 401, and shows the
// server's error message as an alert. Returns the parsed body, or null on failure.
export const useAdminApi = () => {
    const user = useSelector((state) => state.userController.user);
    const dispatch = useDispatch();
    const { logout } = useLogout();
    // useLogout returns a new function every render; keeping it in a ref keeps `request` (and
    // every loader built on it) stable, so effects that depend on them don't refetch in a loop.
    const logoutRef = useRef(logout);
    logoutRef.current = logout;

    const notify = useCallback((msg, type = 'success') => {
        dispatch(alertActions.showAlert({ msg, type }));
    }, [dispatch]);

    const request = useCallback(async (path, { method = 'GET', body, query, silent = false } = {}) => {
        if (!user?.token) return null;
        const qs = query
            ? `?${new URLSearchParams(Object.entries(query).filter(([, v]) => v !== undefined && v !== null && v !== '')).toString()}`
            : '';
        try {
            const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}${path}${qs}`, {
                method,
                headers: {
                    'Content-Type': 'application/json',
                    authorization: `bearer ${user.token}`,
                },
                ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
            });
            const json = res.status === 204 ? {} : await res.json().catch(() => ({}));
            if (res.status === 401) {
                logoutRef.current();
                return null;
            }
            if (!res.ok) {
                if (!silent) notify(json?.error || json?.message || 'حدث خطأ غير متوقع', 'error');
                return null;
            }
            return json;
        } catch (err) {
            if (!silent) notify(err.message || 'تعذر الاتصال بالخادم', 'error');
            return null;
        }
    }, [user?.token, notify]);

    const hasAccess = useCallback((scope) => Boolean(user && (user.access.includes(scope) || user.access.includes('owner'))), [user]);

    return { request, notify, hasAccess, user };
};

export default useAdminApi;
